import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { FullPageSpinner } from "@/components/peeps/ui";
import Login from "@/pages/Login";
import Groups from "@/pages/Groups";
import Activity from "@/pages/Activity";
import Agenda from "@/pages/Agenda";
import Profile from "@/pages/Profile";
import UserProfile from "@/pages/UserProfile";
import AdminUsers from "@/pages/AdminUsers";
import CreateGroup from "@/pages/CreateGroup";
import GroupDetail from "@/pages/GroupDetail";
import GroupSettings from "@/pages/GroupSettings";
import EventDetail from "@/pages/EventDetail";
import Invite from "@/pages/Invite";
import Feedback from "@/pages/Feedback";
import Discover from "@/pages/Discover";
import About from "@/pages/About";
import HowItWorks from "@/pages/HowItWorks";
import Messages from "@/pages/Messages";
import GroupRequests from "@/pages/GroupRequests";
import Conversation from "@/pages/Conversation";
import { GroupGallery, EventGallery, AllGallery } from "@/pages/Gallery";
import "@/App.css";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ minHeight: "100vh" }}><FullPageSpinner /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ minHeight: "100vh" }}><FullPageSpinner /></div>;
  // Signed-in users go to the app (Activity) like everyone else. Admins only stay
  // on the landing page when they explicitly opened it to edit (?admin=1).
  const wantsEdit = new URLSearchParams(window.location.search).get("admin") === "1";
  if (user && !(user.is_app_admin && wantsEdit)) return <Navigate to="/" replace />;
  return <Login />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/about" element={<About />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/invite/:code" element={<Invite />} />
          <Route path="/" element={<RequireAuth><Activity /></RequireAuth>} />
          <Route path="/groups" element={<RequireAuth><Groups /></RequireAuth>} />
          <Route path="/agenda" element={<RequireAuth><Agenda /></RequireAuth>} />
          <Route path="/feedback" element={<RequireAuth><Feedback /></RequireAuth>} />
          <Route path="/discover" element={<RequireAuth><Discover /></RequireAuth>} />
          <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
          <Route path="/user/:id" element={<RequireAuth><UserProfile /></RequireAuth>} />
          <Route path="/messages" element={<RequireAuth><Messages /></RequireAuth>} />
          <Route path="/messages/:id" element={<RequireAuth><Conversation /></RequireAuth>} />
          <Route path="/admin/group-requests" element={<RequireAuth><GroupRequests /></RequireAuth>} />
          <Route path="/admin/users" element={<RequireAuth><AdminUsers /></RequireAuth>} />
          <Route path="/create-group" element={<RequireAuth><CreateGroup /></RequireAuth>} />
          <Route path="/group/:id" element={<RequireAuth><GroupDetail /></RequireAuth>} />
          <Route path="/group/:id/settings" element={<RequireAuth><GroupSettings /></RequireAuth>} />
          <Route path="/group/:id/gallery" element={<RequireAuth><GroupGallery /></RequireAuth>} />
          <Route path="/event/:id" element={<RequireAuth><EventDetail /></RequireAuth>} />
          <Route path="/event/:id/gallery" element={<RequireAuth><EventGallery /></RequireAuth>} />
          <Route path="/gallery/all" element={<RequireAuth><AllGallery /></RequireAuth>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "#20212C",
            border: "1px solid #2C2D3A",
            color: "#F8F9FA",
            fontFamily: "'Space Grotesk', sans-serif",
          },
        }}
      />
    </AuthProvider>
  );
}

export default App;
